import { IBaseEntity, IBaseServerEntity } from './base.interfaces';
import { IServerUser, IUser } from './user.interfaces';
import { JobStatus } from '../consts';
import { IOption } from '../../base';
import { IRangeBlocksValues } from './forms.interfaces';
import { ITag } from './organization.interfaces';

export interface IJobLabelOption extends IOption {
    status: JobStatus;
}

export interface IServerApplyQuestionOption {
    value?: string | number;
    label?: string;
    range_ind?: boolean;
    range_from?: string | null;
    range_to?: string | null;
    is_deleted?: boolean;
    is_expected?: boolean;
}

export interface IApplyQuestionOption extends IOption {
    rangeInd?: boolean;
    rangeFrom?: string | null;
    rangeTo?: string | null;
    isExpected?: boolean;
}

// Server response format for apply questions
export interface IServerApplyQuestionResponse {
    id: number;
    job: number;
    company: number;
    question: string;
    type: QuestionType;
    index?: number;
    is_required?: boolean;
    is_dealbreaker?: boolean;
    options: Array<IServerApplyQuestionOption>;
    dealbreaker_expected_answer?: string;
    dealbreaker_expected_options?: Array<IServerApplyQuestionOption>;
}

// Client format for apply questions
export interface IApplyQuestion {
    id: number;
    jobId: number;
    companyId: number;
    question: string;
    type: QuestionType;
    index?: number;
    isRequired: boolean;
    isDealbreaker: boolean;
    options: Array<IApplyQuestionOption>;
    dealbreakerExpectedAnswer?: string;
    dealbreakerExpectedOptionValues?: Array<string>;
}

export interface IServerJob extends IBaseServerEntity {
    company_overview: string;
    description: string;
    external_job_id: string;
    location: string;
    published_date: string;
    responsibilities: string;
    status: string;
    title: string;
    creator: IServerUser;
    recruiter: IServerUser;
    recruiting_manager: IServerUser;
    job_attributes: Array<IBaseServerJobAttribute>;
    job_benefits: Array<IBaseServerJobBenefits>;
    job_requirement_groups: Array<IBaseServerJobRequirementGroup>;
    statistics: IServerJobCandidatesStatistics;
    job_language: string;
    company_logo_url: string;
    steps: Array<IServerJobSteps>;
    apply_questions?: Array<IServerApplyQuestionResponse>;
    requirement_recommendations?: Array<IServerJobRequirementRecommendation>;
    exhausted_recommendations?: boolean;
    tags?: Array<ITag>;
}

export interface IJob extends IBaseEntity {
    companyOverview?: string;
    description?: string;
    externalJobId?: string;
    location?: string;
    publishedDate?: string;
    responsibilities?: string;
    status?: string;
    title: string;
    recruiterName?: string;
    creator: IUser;
    recruiter?: IUser;
    recruitingManager?: IUser;
    jobAttributes?: Array<IBaseJobAttribute>;
    jobBenefits?: Array<IBaseJobBenefits>;
    jobRequirementGroups?: Array<IBaseJobRequirementGroup>;
    jobLanguage?: string;
    companyLogoUrl?: string;
    steps?: Array<IJobStep>;
    statistics?: IClientJobCandidatesStatistics;
    applyQuestions?: Array<IApplyQuestion>;
    requirementRecommendations?: Array<IServerJobRequirementRecommendation>;
    exhaustedRecommendations?: boolean;
    tags?: Array<ITag>;
}

export interface IServerJobCandidatesStatistics {
    rejected_applicants: number;
    new_applicants: number;
    proceeded_applicants: number;
    total_applicants: number;
}

export interface IServerJobSteps {
    id: string;
    name: string;
    step_owner: IServerUser;
    interviewers: Array<IServerUser>;
}

export interface IClientJobCandidatesStatistics {
    declinedApplicants: number;
    newApplicants: number;
    proceededApplicants: number;
    totalApplicants: number;
}

export interface IJobStep {
    id: string;
    name: string;
    stepOwner: IOption;
    interviewers?: Array<IOption>;
    isFirstStep: boolean;
    isLastStep: boolean;
}

export interface IBaseServerJobAttribute {
    job_attribute_option: IBaseServerJobAttributeOption;
    job_attribute_value: IBaseServerJobAttributeValue;
}

export interface IBaseServerJobAttributeOption {
    id: number;
    name: string;
}

export interface IBaseServerJobAttributeValue {
    id: number;
    name: string;
}

export interface IBaseServerJobBenefits {
    job_benefit_option: IBaseServerJobBenefitsOption;
    job_benefit_value: IBaseServerJobBenefitsValue;
}

export interface IBaseServerJobBenefitsOption {
    id: number;
    name: string;
}

export interface IBaseServerJobBenefitsValue {
    name: string;
}

export interface IBaseServerJobRequirement {
    id: number;
    importance_level: string;
    weight: number;
    range_from: string;
    range_to: string;
    job_requirement_option: IBaseServerJobRequirementsOption;
    is_visible: boolean;
    is_recommended: boolean;
}

export interface IBaseServerJobRequirementsOption {
    id: number;
    name: string;
    range_ind: boolean;
}

export interface IBaseServerJobRequirementGroup {
    id: number;
    group_name: string;
    requirement_options: Array<IBaseServerJobRequirement>;
}

export interface IBaseJobAttribute {
    jobAttributeOption: IBaseJobAttributeOption;
    jobAttributeValue: IBaseJobAttributeValue;
}

export interface IBaseJobAttributeOption {
    id: number;
    name: string;
}

export interface IBaseJobAttributeValue {
    id: number;
    name: string;
}

export interface IBaseJobBenefits {
    jobBenefitsOption: IBaseJobBenefitsOption;
    jobBenefitsValue: IBaseJobBenefitsValue;
}

export interface IBaseJobBenefitsOption {
    id: number;
    name: string;
}

export interface IBaseJobBenefitsValue {
    name: string;
}

export interface IBaseJobRequirement {
    id: number;
    optionId: number;
    optionName: string;
    name: string;
    importanceLevel: string;
    weight: number;
    rangeFrom: string;
    rangeTo: string;
    isVisible: boolean;
    rangeInd: boolean;
}

export enum BaseJobRequirementFields {
    NAME = 'name',
    IMPORTANCE_LEVEL = 'importanceLevel',
    RANGE_FROM = 'rangeFrom',
    RANGE_TO = 'rangeTo',
    REQUIREMENT_GROUP = 'requirementGroup',
    WEIGHT = 'weight',
    IS_VISIBLE = 'isVisible',
}

export interface IBaseJobRequirementForm {
    [BaseJobRequirementFields.NAME]: string;
    [BaseJobRequirementFields.IMPORTANCE_LEVEL]: string;
    [BaseJobRequirementFields.RANGE_FROM]: string;
    [BaseJobRequirementFields.RANGE_TO]: string;
    [BaseJobRequirementFields.REQUIREMENT_GROUP]: string;
    [BaseJobRequirementFields.WEIGHT]: { to: number };
    [BaseJobRequirementFields.IS_VISIBLE]: boolean;
}

export interface IBaseJobRequirementGroup {
    id: number;
    groupName: string;
    requirements: Array<IBaseJobRequirement>;
}

export interface IServerJobStatistics {
    total_applicants: number;
    total_difference: number;
    new_applicants: number;
    new_difference: number;
    proceeded_applicants: number;
    proceeded_difference: number;
    rejected_applicants: number;
    rejected_difference: number;
    average_job_publish: number;
}

export interface IClientJobStatistics {
    totalApplicants: number;
    totalDifference: number;
    newApplicants: number;
    newDifference: number;
    proceededApplicants: number;
    proceededDifference: number;
    rejectedApplicants: number;
    rejectedDifference: number;
    averageJobPublish: number;
}

export interface IStatsCard {
    icon?: React.ReactNode;
    title: string;
    value: string | number | null;
    change?: {
        value: number | null;
        isPositive: boolean;
        isZero: boolean;
    };
    subtitle?: string;
}

export interface ICandidateMetrics {
    label: string;
    value: number;
    onClick: () => void;
    iconName?: string;
}

export interface IStatusOption {
    key: string;
    value: string;
    label: string;
    disabled: boolean;
}

export interface IJobMetadata {
    createdBy: string;
    jobDepartment: string;
    lastEdited: string;
    editedBy: string;
    recruitingManager: string;
    recruiter: string;
}

export enum SAVE_JOB_FORM_VALUES {
    TITLE = 'title',
    LOCATION = 'location',
    EXTERNAL_JOB_ID = 'externalJobId',
    ATTRIBUTE = 'attribute',
    RECRUITING_MANAGER = 'recruitingManager',
    RECRUITER = 'recruiter',
    COMPANY_OVERVIEW = 'companyOverview',
    JOB_OVERVIEW = 'jobOverview',
    RESPONSIBILITIES = 'responsibilities',
    JOB_REQUIREMENTS = 'jobRequirements',
    JOB_BENEFITS = 'jobBenefits',
    STEPS = 'steps',
    STEPS_SELECTED_JOB = 'stepsSelectedJob',
    QUESTIONS = 'questions',
    QUESTIONS_SELECTED_JOB = 'questionsSelectedJob',
}

export interface ISaveJobForm {
    [SAVE_JOB_FORM_VALUES.TITLE]: string;
    [SAVE_JOB_FORM_VALUES.LOCATION]?: string;
    [SAVE_JOB_FORM_VALUES.EXTERNAL_JOB_ID]?: string;
    [SAVE_JOB_FORM_VALUES.ATTRIBUTE]: Array<{ value: IOption | null } | null>;
    [SAVE_JOB_FORM_VALUES.RECRUITING_MANAGER]?: IOption | null;
    [SAVE_JOB_FORM_VALUES.RECRUITER]?: IOption | null;
    [SAVE_JOB_FORM_VALUES.COMPANY_OVERVIEW]?: string;
    [SAVE_JOB_FORM_VALUES.JOB_OVERVIEW]?: string;
    [SAVE_JOB_FORM_VALUES.RESPONSIBILITIES]?: string;
    [SAVE_JOB_FORM_VALUES.JOB_REQUIREMENTS]: Array<ISaveJobRequirement>;
    [SAVE_JOB_FORM_VALUES.JOB_BENEFITS]: Array<ISaveJobBenefit>;
    [SAVE_JOB_FORM_VALUES.STEPS]: Array<IJobStepObject>;
    [SAVE_JOB_FORM_VALUES.STEPS_SELECTED_JOB]?: IOption | null;
    [SAVE_JOB_FORM_VALUES.QUESTIONS]: Array<IJobQuestionObject>;
    [SAVE_JOB_FORM_VALUES.QUESTIONS_SELECTED_JOB]?: IOption | null;
}

export interface IJobAttributeValue {
    id: string;
    name: string;
}

export interface IJobAttribute {
    id: number;
    name: string;
    values: Array<IJobAttributeValue>;
}

export interface IJobAttributeForSave {
    job_attribute_option: string;
    job_attribute_value: string;
}

export interface ISaveJobPayload {
    status?: JobStatus;
    job_attributes?: Array<IJobAttributeForSave>;
    job_requirements?: Array<unknown>; // TODO set the type
    job_benefits?: Array<unknown>; // TODO set the type
    steps?: Array<unknown>; // TODO set the type
    apply_questions?: Array<IJobQuestionObject>; // Raw form data from component
    title?: string;
    description?: string;
    external_job_id?: string;
    location?: string;
    company_overview?: string;
    responsibilities?: string;
    recruiter?: string | undefined;
    recruiting_manager?: string | undefined;
}

// Server-ready payload with formatted apply questions
export interface ISaveJobServerPayload
    extends Omit<ISaveJobPayload, 'apply_questions'> {
    apply_questions?: Array<IServerApplyQuestion>;
}

export interface ISaveJobRequirement {
    requirementId: number | null;
    recommendationId: number | null;
    name: string;
    requirementGroup?: string;
    importanceLevel: string;
    weight: IRangeBlocksValues;
    rangeFrom: string;
    rangeTo: string;
    isVisible: boolean;
    rangeInd?: boolean;
    isAIRecommendation: boolean;
    requirementIndex?: number;
}

export interface ISaveJobBenefit {
    formId: number;
    benefitId?: string;
    name: string;
    isSelected: boolean;
}

export interface IServerJobRequirementOption {
    id: string;
    name: string;
    range_ind: boolean;
    group_name: string;
}

export interface IJobRequirementOption {
    id: string;
    name: string;
    rangeInd: boolean;
    groupName: string;
}

export interface IJobBenefitsOption {
    id: number;
    name: string;
}

export interface IServerCandidateJob {
    id: number;
    title: string;
    description: string;
    location: string;
    published_date: string;
    external_job_id: string;
    job_attributes: Array<IBaseServerJobAttribute>;
}

export interface ICandidateJob {
    id: number;
    title: string;
    description: string;
    location: string;
    publishedDate: string;
    externalJobId: string;
    jobAttributes: Array<IBaseJobAttribute>;
}

export interface IServerJobCandidate {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    image: string | null;
    job: {
        id: number;
        title: string;
    };
    status: string;
    submission_date: string;
    candidate_score: number;
    current_step: number;
    current_step_name: string;
    current_step_status: string;
    current_step_index: number;
    current_step_owner: number | null;
    is_saved: boolean;
    reject_reason: string | null;
    tags: Array<ITag>;
}

export interface IJobStepCandidate {
    id: number;
    candidateId: number;
    candidateName: string;
    candidateScore: number;
    tags: Array<ITag>;
}

export interface IJobCandidatesByStep {
    stepId: number;
    stepName: string;
    stepIndex: number;
    candidates: Array<IJobStepCandidate>;
}

export interface IAssignedJobListRecord {
    id: number;
    title: string;
}

export interface ITrackableSource {
    uuid: string;
    name: string;
}

export interface ITrackableSourceObject {
    [key: string]: string;
}

export interface IServerTrackableSource {
    uuid: string;
    name: string;
}

export enum JobStepObjectFields {
    ID = 'id',
    STEP_NAME = 'stepName',
    STEP_OWNER = 'stepOwner',
    INTERVIEWERS = 'interviewers',
    IS_FIRST_STEP = 'isFirstStep',
    IS_LAST_STEP = 'isLastStep',
}

export interface IJobStepObject {
    [JobStepObjectFields.ID]: string;
    [JobStepObjectFields.STEP_NAME]: string;
    [JobStepObjectFields.STEP_OWNER]: IOption | null;
    [JobStepObjectFields.INTERVIEWERS]: Array<IOption> | null;
    [JobStepObjectFields.IS_FIRST_STEP]: boolean;
    [JobStepObjectFields.IS_LAST_STEP]: boolean;
}

export enum JobStepsFormFields {
    STEPS = 'steps',
    SELECTED_JOB = 'selectedJob',
}

export interface IJobStepsForm {
    [JobStepsFormFields.STEPS]: Array<IJobStepObject>;
    [JobStepsFormFields.SELECTED_JOB]: IOption | null;
}

export enum QuestionType {
    SINGLE_SELECT = 'SINGLE_SELECT',
    MULTIPLE_SELECT = 'MULTIPLE_SELECT',
    LONG_TEXT = 'LONG_TEXT',
    SHORT_TEXT = 'SHORT_TEXT',
    SELECT_DATE = 'SELECT_DATE',
    DATE_RANGE = 'DATE_RANGE',
    BOOLEAN = 'BOOLEAN',
    FILE_UPLOAD = 'FILE_UPLOAD',
    NUMERIC = 'NUMERIC',
}

export interface IQuestionOption extends IOption {
    id?: number;
    isCorrectAnswer?: boolean;
    rangeInd?: boolean;
    rangeFrom?: string | null;
    rangeTo?: string | null;
}

// Server-side apply question format
export interface IServerApplyQuestion {
    id: string;
    index?: number;
    question: string;
    type: QuestionType;
    is_required: boolean;
    is_dealbreaker: boolean;
    options?: Array<IServerApplyQuestionOption>;
    dealbreaker_expected_answer?: string;
    dealbreaker_expected_option_values?: Array<string>;
}

export enum JobQuestionObjectFields {
    ID = 'id',
    QUESTION_TITLE = 'questionTitle',
    QUESTION_TYPE = 'questionType',
    IS_REQUIRED = 'isRequired',
    IS_DEALBREAKER = 'isDealbreaker',
    IS_DATE_RANGE = 'isDateRange',
    DATE_FROM = 'dateFrom',
    DATE_TO = 'dateTo',
    OPTIONS = 'options',
    DELETED_OPTIONS = 'deletedOptions',
    DEALBREAKER_ANSWER = 'dealbreakerAnswer',
    BAND_FROM = 'bandFrom',
    BAND_TO = 'bandTo',
    BAND_OPTION_VALUE = 'bandOptionValue',
}

export interface IJobQuestionObject {
    [JobQuestionObjectFields.ID]: string;
    [JobQuestionObjectFields.QUESTION_TITLE]: string;
    [JobQuestionObjectFields.QUESTION_TYPE]: IOption | null;
    [JobQuestionObjectFields.IS_REQUIRED]: boolean;
    [JobQuestionObjectFields.IS_DEALBREAKER]: boolean;
    [JobQuestionObjectFields.IS_DATE_RANGE]?: boolean;
    [JobQuestionObjectFields.DATE_FROM]?: Date | null;
    [JobQuestionObjectFields.DATE_TO]?: Date | null;
    [JobQuestionObjectFields.OPTIONS]?: Array<IQuestionOption>;
    [JobQuestionObjectFields.DELETED_OPTIONS]?: Array<{ value: string }>;
    [JobQuestionObjectFields.DEALBREAKER_ANSWER]?: string | null;
    [JobQuestionObjectFields.BAND_FROM]?: string | null;
    [JobQuestionObjectFields.BAND_TO]?: string | null;
    [JobQuestionObjectFields.BAND_OPTION_VALUE]?: string | null;
}

export enum JobQuestionsFormFields {
    QUESTIONS = 'questions',
    SELECTED_JOB = 'selectedJob',
}

export interface IJobQuestionsForm {
    [JobQuestionsFormFields.QUESTIONS]: Array<IJobQuestionObject>;
    [JobQuestionsFormFields.SELECTED_JOB]: IOption | null;
}

export interface IServerJobRequirementRecommendation {
    id: number;
    job_requirement_option: number;
    name: string;
    is_new: boolean;
    range_from: string | null;
    range_to: string | null;
    range_ind?: boolean;
    group_name: string;
}
export enum QuestionMode {
    CONFIGURATION = 'configuration',
    SUBMIT = 'submit',
    VIEW = 'view',
}
